import cn from 'classnames';
import { FC, useEffect, useMemo } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button } from 'src/components/common/Button';
import { SvgIcon } from 'src/components/common/SvgIcon';
import { envStrToBoolean, scrollToTop } from 'src/helpers/utils';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { useI18n } from 'src/hooks/use-i18n-translation';
import { CMSManager } from 'src/manager/CMSManager';
import { MediaManager } from 'src/manager/MediaManager';
import { getInfoPages } from 'src/services/cmsApi';
import { openMyAccountModal } from 'src/store/account-modal/actions';
import { setBottomNavigation } from 'src/store/configs/actions';
import { toggleMobileBottomNavigation } from 'src/store/mobile/actions';
import { CmsInfoPagesResponse } from 'src/types/cms/cmsPageTypes';
import { NavigationItem } from 'src/types/res-dto/categories-res';
import { RootState } from 'src/types/store-types';
import './styles.scss';

type Props = {
  settings: {
    type: string; //link
    viewType: string; // row | list
    priority: number;
    styles: Styles;
    providerList?: boolean;
  };
};
const BottomNavigation: FC<Props> = ({ settings }) => {
  const { type, viewType, providerList } = settings;
  const navigate = useNavigate();
  const { locale } = useI18n();

  const { bottomNavigation, categoriesData, navigation } = useSelector((state: RootState) => state.configs);

  const _openAccountModal = useConnectedAction(openMyAccountModal);
  const _toggleMobileBottomNavigation = useConnectedAction(toggleMobileBottomNavigation);
  const _setBottomNavigation = useConnectedAction(setBottomNavigation);

  const FEATURED_PROVIDERS = [
    'pragmaticplay',
    'evolution',
    'evoplay',
    'pgsoft',
    'amigogaming',
    '3oaks',
    'amusnet',
    'egtdigital',
    'spribe',
    'popok',
    'nolimitcity',
    'habanero',
  ];

  const normalize = (s: string): string => s.toLowerCase().replace(/[\s\-_]/g, '');

  const providers = useMemo(() => {
    if (!providerList) return [];
    const categories = navigation?.filter((f: NavigationItem) => f?.type === 'category');
    const providerSet: Set<string> = new Set();
    categories?.forEach((f: NavigationItem) => {
      categoriesData?.[f?.name]?.providers?.forEach((p: string) => providerSet.add(p));
    });
    return Array.from(providerSet).filter((p) => FEATURED_PROVIDERS.includes(normalize(p)));
  }, [providerList, categoriesData, navigation]);

  const handleNavItemClick = (name: string, slug: string): void => {
    const navigateToPage = envStrToBoolean(process.env.REACT_APP_BOTTOM_NAVIGATION_TO_PAGES);
    navigateToPage ? redirectToInfoAndRulesPage(slug) : openInfoAndRulesModal(name);
  };

  const openInfoAndRulesModal = (name: string): void => {
    _openAccountModal({ section: 'info_and_rules', subSection: name });

    isMobileOnly && _toggleMobileBottomNavigation();
  };

  const redirectToInfoAndRulesPage = (name: string): void => {
    const path = `/${name}`;
    path && navigate(path);
    path && scrollToTop();
  };

  const getStaticPageData = async (): Promise<void> => {
    const res: CmsInfoPagesResponse = await getInfoPages(locale);

    if (res?.result?.length) {
      const _ = CMSManager.inhouseCms.getInfoAndRulesData(res.result);

      _setBottomNavigation(_);
    } else {
      _setBottomNavigation([]);
      console.error('bottom navigation from csm is not found');
    }
  };

  useEffect(() => {
    if (locale) {
      getStaticPageData();
    }
  }, [locale]);

  return (
    <div
      className={cn('bottom_navigation_wrapper', {
        ['rules_row']: viewType === 'row',
        ['rules_list']: viewType === 'list',
      })}
    >
      {bottomNavigation?.map((navItem: any, index: number) => (
        <Button
          className={cn('rule', { ['link']: type === 'link' })}
          key={navItem.slug || navItem.name || navItem.title || index}
          onClick={() => handleNavItemClick(navItem.name, navItem.slug)}
        >
          {navItem.title}
        </Button>
      ))}
      {providerList && providers.length > 0 && (
        <div className="bottom_navigation_providers">
          {providers.map((provider: string) => (
            <div className="bottom_navigation_provider" key={provider}>
              <SvgIcon
                src={MediaManager.getSrcFromGeneralMediaAssets(
                  `${provider.toLowerCase()}.svg`,
                  `/providerIcons/BlackMode/`
                )}
                name={provider}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
export default BottomNavigation;
